import { useFocusVisible, useHover } from '@fullstory/react-hooks';
import { classNames } from '@fullstory/util/fmt';
import { UID } from '@fullstory/util/uid';
import React, { ComponentProps, forwardRef, useEffect, useState } from 'react';
import { Flex } from '../../legacy/Flex';
import { identifyComponent } from '../../util';
import { ButtonBase } from '../ButtonBase';
import { Text } from '../Text';
import css from './index.module.scss';

interface LabelProps {
  text: string;
  appearance?: React.ComponentProps<typeof Text>['appearance'];
  noWrap?: React.ComponentProps<typeof Text>['noWrap'];
}

export interface ToggleProps {
  testId?: string;
  /** `true` if Toggle is on the 'on' position and `false` if Toggle is in the 'off' position */
  checked: boolean;
  /** Function called when toggling on/off */
  onToggle: (value: boolean) => void;
  /** Text to display left of the Toggle. Accepts an object to change font variant */
  label?: string | LabelProps;
  /**
   * Which side of the Toggle to display the label.
   * Defaults to `left`.
   * */
  labelPosition?: 'left' | 'right' | 'hidden';
  /** If `true`, toggling is disabled and component will be greyed out */
  disabled?: boolean;
  /**
   * NOTE: The className prop shouldn't be used for custom styles. It exists as an escape hatch for
   * very specific cases (e.g. fs_app_domain_toggle)
   */
  className?: string;
  /** If `true`, the toggle will span the full width of its container. */
  fullWidth?: boolean;
  alignItems?: ComponentProps<typeof Flex>['alignItems'];
}

/**
 * Toggles provide a way for users to turn a single parameter on or off.
 */
export const Toggle = forwardRef<HTMLElement, ToggleProps>(
  (
    {
      testId = 'toggle',
      checked,
      disabled = false,
      onToggle,
      label,
      labelPosition = 'left',
      className,
      fullWidth = false,
      alignItems = 'flex-start',
    },
    ref,
  ) => {
    const [id, setId] = useState<string>();
    const { hovered, hoverProps } = useHover();
    const { focusVisible, ...focusProps } = useFocusVisible();
    let labelColor: ComponentProps<typeof Text>['color'] = 'default';
    if (disabled) {
      labelColor = 'disabled';
    } else if (hovered || focusVisible) {
      labelColor = 'emphasized';
    }

    const hiddenProps: React.HTMLAttributes<HTMLLabelElement> =
      labelPosition === 'hidden' ? { style: { display: 'none' }, 'aria-hidden': true } : {};

    useEffect(() => {
      setId(UID());
    }, []);

    if (id == undefined) return null;
    const labelId = id ? `label-${id}` : undefined;
    const toggleId = id ? `toggle-${id}` : undefined;

    return (
      <div
        data-component="Toggle"
        className={classNames(
          css.wrapper,
          { [css.focused]: focusVisible },
          { [css.hovered]: hovered },
        )}
        {...identifyComponent('toggle')}
        {...hoverProps}
      >
        <Flex
          alignItems={alignItems}
          justifyContent={fullWidth ? 'space-between' : 'center'}
          reverse={labelPosition === 'right'}
          gap={8}
        >
          {label && (
            <label id={labelId} htmlFor={toggleId} {...hiddenProps}>
              <Text
                tag="span"
                appearance={
                  typeof label === 'string' || !label.appearance ? 'bodyMedium' : label.appearance
                }
                color={labelColor}
                noWrap={typeof label === 'string' ? undefined : label.noWrap}
              >
                {typeof label === 'string' ? label : label.text}
              </Text>
            </label>
          )}
          <ButtonBase
            role="switch"
            aria-checked={checked}
            aria-labelledby={labelId}
            disabled={disabled}
            id={toggleId}
            testId={testId}
            ref={ref}
            className={classNames(className, css.toggle, css.primary, {
              [css.checked]: checked,
              [css.disabled]: disabled,
              [css.hovered]: hovered,
              [css.focused]: focusVisible,
              [css.withLabel]: !!label,
            })}
            onClick={() => !disabled && onToggle(!checked)}
            {...focusProps}
          >
            <div
              className={classNames(css.slider, {
                [css.checked]: checked,
                [css.disabled]: disabled,
              })}
            />
          </ButtonBase>
        </Flex>
      </div>
    );
  },
);
Toggle.displayName = 'Toggle';
